/**
 * File: apps/qbench-cp/reactapp/src/shared/components/Search/Search.jsx
 *
 * Purpose: Renders the dashboard's main search input, driving the shared search filter and
 *     redirecting to the orders list to show results.
 *
 * Scope: Shared Search component used on the customer portal dashboard.
 *
 * Tags: customer-portal
 *
 * Overview: Keeps a local search string in sync with the DashboardContext's mainSearch filter,
 *     dispatching updateMainSearch and navigating to /portal/orders when the user presses Enter
 *     or clicks the search icon — which leads the field, the way a search input conventionally
 *     reads — and re-running the search when the field is cleared while a search was previously
 *     active. The placeholder text is generated dynamically from order and sample entity display
 *     names plus the sample fields the search actually matches on, resolved by
 *     useSearchableSampleFields (the customer's own division, else Global) so the placeholder
 *     cannot name fields the search ignores. A clear icon resets the field and notifies the
 *     optional onChange prop as the user types. When showMatchingSamplesToggle is set (only the
 *     orders toolbar passes it) a samples-only toggle renders as a flask IconButton inside the
 *     field's trailing edge — primary-coloured while active — driving
 *     appState.displaySettings.matchingSamplesOnly; the other mount points (home header, mobile
 *     top nav) render just the input.
 *
 * Exports: Search (default export).
 */

import { faFlask } from "@fortawesome/free-solid-svg-icons";
import { useAppStore } from "App/store";
import closeIcon from "assets/images/svg/close-small.svg";
import searchIcon from "assets/images/svg/search.svg";
import { updateMainSearch } from "Dashboard/reducers/filters";
import { DashboardContext } from "Dashboard/state";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Input } from "shared/components/common/Input";
import IconButton from "shared/components/IconButton";
import { EntityType } from "shared/constants/entityTypes";
import { useEntityDisplayName } from "shared/hooks/misc/fields";
import useSearchableSampleFields from "shared/hooks/misc/useSearchableSampleFields";
import styled, { useTheme } from "styled-components";

// Single flex child (`.searchWrapper` is what every consumer sizes), so the parent flex layout
// keeps seeing one child no matter what the input row carries.
const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSearch = styled.div`
  height: 32px;
  background-color: ${({ theme }) =>
    theme.color.palette.background.paper.defaultLight};
`;

// The field is only 32px tall, so the button carries no vertical padding of its own. Styling the
// component (rather than a `.btn` class) keeps this off react-bootstrap's internals.
const SamplesOnlyToggle = styled(IconButton)`
  display: flex;
  align-items: center;
  padding: 0 6px;
  line-height: 1;
`;

const Search = ({
  onChange = () => {},
  showMatchingSamplesToggle = false,
  ...props
}) => {
  const [orderSingular] = useEntityDisplayName(EntityType.ORDER);
  const [sampleSingular, samplePlural] = useEntityDisplayName(
    EntityType.SAMPLE
  );
  const { appState, appStateDispatch } = useAppStore();
  const { matchingSamplesOnly } = appState.displaySettings;
  const theme = useTheme();

  const handleMatchingToggle = React.useCallback(() => {
    appStateDispatch({
      type: "SET_DISPLAY_SETTINGS",
      payload: { matchingSamplesOnly: !matchingSamplesOnly },
    });
  }, [appStateDispatch, matchingSamplesOnly]);
  const fields = useSearchableSampleFields();

  const {
    state: {
      filters: { mainSearch },
    },
    dispatch,
  } = React.useContext(DashboardContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = React.useState(mainSearch || "");
  React.useEffect(() => {
    if (typeof value === "string") {
      setSearch(mainSearch);
    }
  }, [mainSearch]);

  React.useEffect(() => {
    if (search === "" && mainSearch) {
      onEnter();
    }
  }, [search]);

  const redirectToResultPage = React.useCallback(() => {
    const path = "/portal/orders";
    search && pathname !== path && navigate(path);
  }, [search, pathname, navigate]);

  const onEnter = React.useCallback(() => {
    dispatch(updateMainSearch(search));
    redirectToResultPage();
  }, [dispatch, search, redirectToResultPage]);

  const handleOnChange = React.useCallback(
    (e) => {
      const value = e.target.value;
      setSearch(value);
      onChange(value);
    },
    [onChange]
  );

  const handleOnEnter = React.useCallback(
    (e) => {
      if (e.type === "keydown" && e.key === "Enter") {
        onEnter();
      } else if (e.type === "click" && e.currentTarget.id === "inputSubmit") {
        onEnter();
      }
    },
    [onEnter]
  );

  const onClearIconClick = React.useCallback(() => {
    if (search.length) {
      setSearch("");
      onChange("");
    }
  }, [onChange]);

  const generatePlaceholder = () => {
    // Name every field the search resolves to, so the placeholder stays a promise the
    // backend keeps. Joining a list also drops the trailing comma the old copy left
    // behind when a customer's config resolved no searchable sample fields at all.
    const searchable = [
      `${orderSingular} ID`,
      `${sampleSingular} ID`,
      ...fields.map((field) => field.displayName).filter(Boolean),
    ];
    return props.placeholder || `Search ${searchable.join(", ")} or others`;
  };

  const matchingToggleLabel = `Only show matching ${samplePlural || "samples"}`;

  return (
    <SearchWrapper className="searchWrapper">
      <StyledSearch>
        <Input
          {...props}
          type="text"
          value={search}
          onChange={handleOnChange}
          onEnter={handleOnEnter}
          onIconClick={handleOnEnter}
          onClearIconClick={onClearIconClick}
          placeholder={generatePlaceholder()}
          icon={searchIcon}
          iconPosition="leading"
          clearIcon={closeIcon}
          trailingAction={
            showMatchingSamplesToggle && (
              <SamplesOnlyToggle
                id="matchingSamplesToggle"
                icon={faFlask}
                color={
                  matchingSamplesOnly
                    ? theme.color.palette.primary.main
                    : "#666"
                }
                tooltip={matchingToggleLabel}
                aria-label={matchingToggleLabel}
                aria-pressed={matchingSamplesOnly}
                onClick={handleMatchingToggle}
              />
            )
          }
        />
      </StyledSearch>
    </SearchWrapper>
  );
};

export default Search;
